import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const PUBLIC_PATHS = "rom|prisar|kalender|arrangement|leig-rom|arranger|kontakt|om-oss|omradet|spor-oss";
// 1: **bold**  2: [label](href)  3: prefix before a bare path  4: bare /path
const INLINE = new RegExp(`(\\*\\*[^*]+\\*\\*)|(\\[[^\\]]+\\]\\([^)]+\\))|(^|[\\s(«])(\\/(?:${PUBLIC_PATHS})(?:\\/[\\w-]+)?)(?=[\\s.,;:!?)»]|$)`, "g");

const linkCls = "u-link font-medium text-foreground";

const renderInline = (text, keyPrefix) => {
  const out = [];
  let last = 0;
  let m;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [raw, bold, mdLink, prefix, path] = m;
    const key = `${keyPrefix}-${m.index}`;
    if (bold) {
      out.push(<strong key={key} className="font-semibold text-foreground">{bold.slice(2, -2)}</strong>);
    } else if (mdLink) {
      const mm = mdLink.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = mm?.[2] || "";
      const label = mm?.[1] || href;
      out.push(href.startsWith("/")
        ? <Link key={key} to={href} className={linkCls}>{label}</Link>
        : <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={linkCls}>{label}</a>);
    } else if (path) {
      if (prefix) out.push(prefix);
      out.push(<Link key={key} to={path} className={linkCls}>{path}</Link>);
    } else {
      out.push(raw);
    }
    last = m.index + raw.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

/** Tiny, safe markdown subset for assistant replies: paragraphs, «- » lists, **bold**, [text](/path) and bare /paths. */
export const RichText = ({ text = "", className }) => {
  const lines = String(text).replace(/\r/g, "").split("\n");
  const blocks = [];
  let list = null;
  let para = [];
  const closePara = () => { if (para.length) { blocks.push({ type: "p", lines: para }); para = []; } };
  const closeList = () => { if (list) { blocks.push(list); list = null; } };
  lines.forEach((ln) => {
    const t = ln.trim();
    if (!t) { closePara(); closeList(); return; }
    const li = t.match(/^(?:[-•*]|\d+[.)])\s+(.*)$/);
    if (li) { closePara(); if (!list) list = { type: "ul", items: [] }; list.items.push(li[1]); return; }
    // indented line right after a bullet = continuation of that bullet
    if (list && /^\s{2,}/.test(ln)) { list.items[list.items.length - 1] += `\n${t}`; return; }
    closeList();
    para.push(t.replace(/^#+\s*/, ""));
  });
  closePara(); closeList();
  return (
    <div className={cn("space-y-2.5 text-[15px] leading-[1.55]", className)}>
      {blocks.map((b, i) => b.type === "ul" ? (
        <ul key={i} className="space-y-1.5 pl-4">
          {b.items.map((it, j) => <li key={j} className="relative before:absolute before:-left-4 before:top-[0.68em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-sol">{it.split("\n").map((part, k) => <span key={k}>{k > 0 && <br />}{renderInline(part, `${i}-${j}-${k}`)}</span>)}</li>)}
        </ul>
      ) : (
        <p key={i}>{b.lines.map((l, j) => <span key={j}>{j > 0 && <br />}{renderInline(l, `${i}-${j}`)}</span>)}</p>
      ))}
    </div>
  );
};
